/**
 * Phone-camera barcode scanner (BUILD PROMPT Phase 3, S6.2). Rear-camera staff scanning.
 *
 * - Native BarcodeDetector first, feature-detected. No decoder dependency is bundled: when
 *   the browser has no detector, this component says so and the hardware/manual fallback
 *   (BarcodeInput) keeps working. Zero new npm dependencies, no CDN, no CSP change.
 * - Decodes locally on the device. Only the decoded barcode string leaves this component,
 *   via onBarcode into the normal product-lookup path. Raw frames are never logged,
 *   uploaded or stored anywhere.
 * - Covers EAN-13, EAN-8, UPC-A, UPC-E and Code 128 (the retail minimum). Decoded text is
 *   handed to lookup as opaque text: QR payloads are never opened as URLs.
 * - Same label held in view is one presentation (local suppression + a Scan Again button for
 *   a deliberate second unit). The scan coordinator ALSO suppresses repeat camera frames,
 *   so double delivery cannot double-add even if both layers see the frame.
 * - Media tracks, the decode loop and the detector are released on Stop, Close, unmount
 *   (navigation/logout) and while the page is hidden. Capture pauses while `paused` is set,
 *   which the integrator wires to payment/modals (see the integrator snippet in the report).
 * - Camera permission in deployment headers stays narrow (camera=(self)); this component
 *   needs nothing broader. Microphone is never requested.
 *
 * Inline styles only: apps/web/src/index.css belongs to another stream and is untouched.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { tr, useT } from '../i18n';

/** Retail-minimum decode formats requested from the native detector. */
export const CAMERA_SCAN_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'];

/** How often a preview frame is offered to the detector. */
export const CAMERA_DECODE_INTERVAL_MS = 250;

/** Largest frame width offered to the detector (keeps per-frame cost bounded). */
export const CAMERA_MAX_FRAME_WIDTH = 640;

interface NativeDetectedBarcode {
  rawValue: string;
}

interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<NativeDetectedBarcode[]>;
}

type NativeBarcodeDetectorCtor = new (options?: { formats?: string[] }) => NativeBarcodeDetector;

declare global {
  interface Window {
    BarcodeDetector?: NativeBarcodeDetectorCtor;
  }
}

export type CameraFailureKind =
  | 'unsupported'
  | 'denied'
  | 'none'
  | 'in-use'
  | 'decoder'
  | 'unknown';

export interface CameraFailure {
  kind: CameraFailureKind;
  message: string;
}

interface CameraDevice {
  deviceId: string;
  label: string;
}

interface ZoomCaps {
  min: number;
  max: number;
  step: number;
  value: number;
}

interface Props {
  /** A decoded presentation: route into the scan coordinator with source 'camera'. */
  onBarcode: (code: string) => void;
  onClose: () => void;
  /** Pause decode ticks (payment sheet / modal open) without tearing down the stream. */
  paused?: boolean;
}

/** False outside secure contexts / devices without any camera API. */
export function isCameraCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/** True when the browser ships a native detector (Chrome/Edge Android, some desktop). */
export function isNativeBarcodeDetectorAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.BarcodeDetector === 'function';
}

function failureMessage(kind: CameraFailureKind): string {
  switch (kind) {
    case 'unsupported':
      return tr('camera.unsupported');
    case 'denied':
      return tr('camera.denied');
    case 'none':
      return tr('camera.none');
    case 'in-use':
      return tr('camera.inUse');
    case 'decoder':
      return tr('camera.decoder');
    case 'unknown':
      return tr('camera.unknown');
  }
}

function toFailure(error: unknown): CameraFailureKind {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    if (error.name === 'NotAllowedError') return 'denied';
    if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') return 'none';
    if (error.name === 'NotReadableError' || error.name === 'AbortError') return 'in-use';
  }
  return 'unknown';
}

export function CameraScanner({ onBarcode, onClose, paused = false }: Props) {
  const t = useT();
  const [phase, setPhase] = useState<'idle' | 'starting' | 'scanning' | 'stopped'>('idle');
  const [failure, setFailure] = useState<CameraFailure | null>(null);
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoom] = useState<ZoomCaps | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<NativeBarcodeDetector | null>(null);
  const timerRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastCodeRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const onBarcodeRef = useRef(onBarcode);
  onBarcodeRef.current = onBarcode;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const releaseStream = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    detectorRef.current = null;
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
    }
    const video = videoRef.current;
    if (video) video.srcObject = null;
  }, []);

  const stop = useCallback(() => {
    releaseStream();
    lastCodeRef.current = null;
    if (mountedRef.current) {
      setLastCode(null);
      setTorchOn(false);
      setPhase('stopped');
    }
  }, [releaseStream]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      releaseStream();
    };
  }, [releaseStream]);

  async function decodeTick(): Promise<void> {
    if (pausedRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    const video = videoRef.current;
    const detector = detectorRef.current;
    if (!video || !detector || video.readyState < 2 || video.videoWidth === 0) return;
    let canvas = canvasRef.current;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvasRef.current = canvas;
    }
    const scale = Math.min(1, CAMERA_MAX_FRAME_WIDTH / video.videoWidth);
    canvas.width = Math.max(1, Math.floor(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.floor(video.videoHeight * scale));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    let found: NativeDetectedBarcode[];
    try {
      found = await detector.detect(canvas);
    } catch {
      return;
    }
    if (pausedRef.current || !mountedRef.current) return;
    const first = found[0];
    const code = typeof first?.rawValue === 'string' ? first.rawValue.trim() : '';
    if (code === '' || code === lastCodeRef.current) return;
    lastCodeRef.current = code;
    setLastCode(code);
    onBarcodeRef.current(code);
  }

  async function start(): Promise<void> {
    if (!isCameraCaptureSupported()) {
      setFailure({ kind: 'unsupported', message: failureMessage('unsupported') });
      return;
    }
    setFailure(null);
    setPhase('starting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: selectedId !== '' ? { deviceId: { exact: selectedId } } : { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    } catch (error) {
      if (!mountedRef.current) return;
      const kind = toFailure(error);
      setFailure({ kind, message: failureMessage(kind) });
      setPhase('idle');
      return;
    }
    if (!mountedRef.current) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    try {
      const listed = await navigator.mediaDevices.enumerateDevices();
      const cameras = listed
        .filter((d) => d.kind === 'videoinput')
        .map((d, index) => ({ deviceId: d.deviceId, label: d.label !== '' ? d.label : 'Camera ' + (index + 1) }));
      setDevices(cameras);
    } catch {
      // Labels need a granted permission; scanning works without the chooser.
    }
    const Ctor = window.BarcodeDetector;
    if (typeof Ctor !== 'function') {
      for (const track of stream.getTracks()) track.stop();
      setFailure({ kind: 'decoder', message: failureMessage('decoder') });
      setPhase('idle');
      return;
    }
    try {
      detectorRef.current = new Ctor({ formats: CAMERA_SCAN_FORMATS });
    } catch {
      for (const track of stream.getTracks()) track.stop();
      setFailure({ kind: 'decoder', message: failureMessage('decoder') });
      setPhase('idle');
      return;
    }
    streamRef.current = stream;
    const video = videoRef.current;
    if (video) {
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        // Autoplay policy: the preview starts on the next user gesture; ticks guard readiness.
      }
    }
    const track = stream.getVideoTracks()[0];
    if (track && typeof track.getCapabilities === 'function') {
      const caps = track.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean;
        zoom?: { min: number; max: number; step: number };
      };
      setTorchSupported(caps.torch === true);
      if (caps.zoom) {
        const current = (track.getSettings() as MediaTrackSettings & { zoom?: number }).zoom;
        setZoom({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step, value: current ?? caps.zoom.min });
      } else {
        setZoom(null);
      }
    } else {
      setTorchSupported(false);
      setZoom(null);
    }
    setPhase('scanning');
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      void decodeTick();
    }, CAMERA_DECODE_INTERVAL_MS);
  }

  async function setTorch(next: boolean): Promise<void> {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] } as unknown as MediaTrackConstraints);
      if (mountedRef.current) setTorchOn(next);
    } catch {
      // Torch is best-effort; the scan region and fallback stay available.
    }
  }

  async function setZoomLevel(next: number): Promise<void> {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: next }] } as unknown as MediaTrackConstraints);
      setZoom((prev) => (prev === null ? prev : { ...prev, value: next }));
    } catch {
      // Zoom is best-effort; decoding continues at the current level.
    }
  }

  function rescan(): void {
    lastCodeRef.current = null;
    setLastCode(null);
  }

  function close(): void {
    stop();
    onClose();
  }

  const scanning = phase === 'scanning';

  return (
    <div data-testid="camera-scanner" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ position: 'relative', background: '#0b1220', borderRadius: 12, overflow: 'hidden' }}>
        <video
          ref={videoRef}
          data-testid="camera-video"
          muted
          playsInline
          style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: scanning ? 'block' : 'none' }}
        />
        {scanning ? (
          <div aria-hidden style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '70%', height: '38%', border: '2px solid #22c55e', borderRadius: 8 }} />
          </div>
        ) : null}
        {!scanning ? (
          <div style={{ padding: 24, color: '#cbd5e1', fontSize: 14 }}>
            {t('camera.preview')}
          </div>
        ) : null}
      </div>
      {failure ? (
        <div data-testid="camera-error" role="alert" style={{ background: '#fef2f2', color: '#991b1b', borderRadius: 8, padding: 10, fontSize: 13 }}>
          {failure.message}
        </div>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {!scanning ? (
          <button type="button" data-testid="camera-start" className="btn btn-primary" onClick={() => { void start(); }}>
            {phase === 'starting' ? t('camera.starting') : t('camera.start')}
          </button>
        ) : (
          <button type="button" data-testid="camera-stop" className="btn" onClick={stop}>
            {t('camera.stop')}
          </button>
        )}
        {devices.length > 1 ? (
          <select
            data-testid="camera-select"
            aria-label={t('camera.camera')}
            value={selectedId}
            disabled={scanning}
            onChange={(event) => setSelectedId(event.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value="">{t('camera.rear')}</option>
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        ) : null}
        {scanning && torchSupported ? (
          <button
            type="button"
            data-testid="camera-torch"
            className="btn"
            aria-pressed={torchOn}
            onClick={() => { void setTorch(!torchOn); }}
          >
            {torchOn ? t('camera.torchOff') : t('camera.torchOn')}
          </button>
        ) : null}
        {scanning && zoom ? (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            {t('camera.zoom')}
            <input
              type="range"
              data-testid="camera-zoom"
              min={zoom.min}
              max={zoom.max}
              step={zoom.step}
              value={zoom.value}
              onChange={(event) => { void setZoomLevel(Number(event.target.value)); }}
            />
          </label>
        ) : null}
        {scanning && lastCode !== null ? (
          <button type="button" data-testid="camera-rescan" className="btn" onClick={rescan}>
            {t('camera.again')}
          </button>
        ) : null}
        <button type="button" data-testid="camera-close" className="btn" onClick={close}>
          {t('common.close')}
        </button>
      </div>
      {lastCode !== null ? (
        <div data-testid="camera-last" style={{ fontSize: 13, color: '#334155' }}>
          {t('camera.last')} {lastCode}
        </div>
      ) : null}
      {paused && scanning ? (
        <div style={{ fontSize: 13, color: '#92400e' }}>{t('camera.paused')}</div>
      ) : null}
    </div>
  );
}
